import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BlurScrollEffect } from '../effects/blurScrollEffect';
import '../styles/partners.scss';
import { PROJECTS } from '../data/projects';
import partnersOgImage from '../assets/images/partners-og.png';
import newIcon1 from '../assets/images/newicon1.svg';
import newIcon2 from '../assets/images/newicon2.svg';
import newIcon3 from '../assets/images/newicon3.svg';
import newIcon1Red from '../assets/images/newicon1red.svg';
import newIcon2Red from '../assets/images/newicon2red.svg';
import newIcon3Red from '../assets/images/newicon3red.svg';
import newIcon4 from '../assets/images/newicon4.svg';
import newIcon5 from '../assets/images/newicon5.svg';
import newIcon6 from '../assets/images/newicon6.svg';
import newIcon7 from '../assets/images/newicon7.svg';
import newIcon8 from '../assets/images/newicon8.svg';

gsap.registerPlugin(ScrollTrigger);

const seoDescription = 'Senior design that speaks developer, hits the ground running, and never needs hand-holding. Your clients get great design. You get the credit. Everyone wins. Your white label design partner.';
const seoTitle = 'Agency Partners | Heretic Studio';

const Partners = () => {
  const blurTextRef = useRef(null);
  const [activeFaq, setActiveFaq] = useState(null);
  
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'Can you handle client communications?',
      answer: "Absolutely. I'm comfortable dealing directly with clients — managing expectations, leading feedback sessions, and keeping projects moving without you needing to play middleman. If you'd rather keep me behind the curtain, that works too."
    },
    {
      question: 'What do you need from us to hit the ground running?',
      answer: "A brief, any existing brand assets or guidelines, and a quick call to fill in the gaps. That's genuinely it. I'm used to working with whatever exists — even if it's rough — and turning it into something we can move forward with fast."
    },
    {
      question: 'How do you hand off design work to developers?',
      answer: "Everything lives in a shared Figma file, organised and annotated so developers can build from it without needing a guided tour. Spacing, components, interactions — all spec'd out clearly. The goal is a handover that answers questions before they're asked and keeps the build phase moving without constant back-and-forth."
    },
    {
      question: 'Are you available as a white label design partner?',
      answer: "Absolutely. I'm happy to work entirely under your agency's banner. As far as your clients are concerned, I'm part of your team. It's a setup I actively enjoy — you bring the client relationship, I bring the design expertise, and the work goes out the door with your name on it. Everyone's happy."
    },
    {
      question: 'What happens if something unexpected delays the project?',
      answer: "Honestly? It happens. Clients go quiet, priorities shift, life gets in the way. When it does, we have a conversation, figure out what's changed, and adjust the timeline together. No drama, no penalty clauses waved in anyone's face — just a practical reset and a clear path forward."
    },
    {
      question: "How do you handle projects where the brief isn't fully defined yet?",
      answer: "That's half the job. I'll work with you to pin down a clear scope — deliverables, timelines, what's in and what's firmly out — that you're confident in and your client can sign off on. A well-defined brief upfront is the best protection either of us has against scope creep later."
    },
    {
      question: 'What happens after handover to devs?',
      answer: "I typically loop developers in during the design process so nothing lands in their lap as a surprise, and everything is buildable before it's signed off. After handover I'm happy to stay close — answering questions, reviewing builds, and making sure what ends up in the browser actually matches what we designed. Consider me available until it's shipped properly."
    },
    {
      question: 'Can you build as well as design?',
      answer: 'Yes — and it\'s one of the things that makes working together easier. I have hands-on experience building sites and web apps in Wordpress, Webflow, React, and JavaScript. That means I can take a project from design all the way through to build, or simply step in at the development stage if that\'s where you need the support.'
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on scope and complexity. A brand identity might take 4-6 weeks, while a full digital product could be 3-6 months. We always start with a discovery phase to define scope and set realistic expectations.'
    },
    {
      question: "What's your design process like?",
      answer: "We follow a collaborative process: discovery → research → strategy → design → testing → delivery. You're involved at every stage, with regular check-ins and opportunities for feedback to ensure we're building the right thing together."
    }
  ];
  
  const carouselItems = useMemo(
    () =>
      PROJECTS.map((project) => {
        const heroBlock = project.blocks.find((block) => block.type === 'image');
        return {
          slug: project.slug,
          title: project.title,
          subtitle: project.subtitle,
          image: heroBlock ? heroBlock.src : null,
        };
      }).filter((project) => project.image),
    []
  );

  useEffect(() => {
    // Initialize blur scroll effect
    if (blurTextRef.current) {
      new BlurScrollEffect(blurTextRef.current);
    }

    // Disable scroll-based animations on small screens so content is immediately visible
    if (typeof window !== 'undefined' && window.matchMedia) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        return;
      }
    }

    const rows = document.querySelectorAll('.section.portfolio .row');
    const heroTitle = document.querySelector('.partners-hero__title');
    if (!rows.length || !heroTitle) return;

    const animations = [];

    rows.forEach((row) => {
      const items = row.querySelectorAll('.portfolio-preview');
      if (!items.length) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.from(items, {
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.15,
      });

      animations.push(tl);
    });

    return () => {
      animations.forEach((tl) => {
        if (tl.scrollTrigger) {
          tl.scrollTrigger.kill();
        }
        tl.kill();
      });
    };
  }, []);

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        ogTitle={seoTitle}
        ogDescription={seoDescription}
        ogImage={partnersOgImage}
        author="Heretic Studio"
        type="article"
      />
      <div className="partners">
        <section className="partners-hero">
          <div className="hero-content">
            <h1 className="partners-hero__title">AGENCY PARTNERS</h1>
            <p className="partners-hero__subtitle">
              Your new best friend
            </p>
          </div>
        </section>

        <section className="section intro">
        <div className="wrapper grid-display">
          <div className="row">
            <div className="col-12">
              <section className="content">
                {/* Desktop: animated blur text */}
                <div
                  className="large center-text introtext introtext--desktop"
                 
                >
                  
                  
                  Design support that speaks developer, looks and works great, and hits the ground running
                
                  <p>I've worked across the full project lifecycle — scoping, deliverables, design assets, site builds, client relationships, budget, the works. Which means I know how agencies operate, what dev teams actually need, and how to be useful without creating extra work for anyone. Your clients get great design. You get the credit. Everyone wins. UX, web-design, prototyping, end-to-end product design — wherever the gap is, I'll fill it seamlessly.</p>
                </div>
                <div className="intro-cta">
                  <Link to="/new-project" className="intro-button mono">
                    Get in touch
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      <section className="section intro">
        <div className="wrapper grid-display">
          <div className="row cards-row">
            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon1} alt="Strategic Planning icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">Start with a conversation, not a contract</h3>
                  <p className="strategy-card-text">Share your project brief — even if "it's a bit vague right now" — and I'll shape it into a project plan worth building from. Requirements, timeline, deliverables: pinned down without the pain.</p>
                </div>
              </div>
            </div>
            
            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon2} alt="User-Centered Design icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">Design you and the client can see, click, and sign off on.</h3>
                  <p className="strategy-card-text">No mystery, no "trust the process" theatre. I put real mockups in front of you early — designed to test assumptions, spark decisions, and give your stakeholders something to get excited about.</p>
                </div>
              </div>
            </div>
            
            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon3} alt="Innovation First icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">Design files your developers won't immediately close in despair.</h3>
                  <p className="strategy-card-text">Redlines, design tokens, annotated specs — whatever your dev team needs, I've got your back. And because I understand how things get built, I design things that are actually buildable. Imagine that.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section faq">
        <div className="wrapper">
          <div className="row">

            <div className="col-3">
              <div className="blur-text blur-text--1 large introtext">
                Details              
              </div>
            </div>

            <div className="col-1">
              
            </div>

            <div className="col-8">
              <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className={`faq-item ${activeFaq === index ? 'active' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <h3>{faq.question}</h3>
                    <span className="faq-toggle">{activeFaq === index ? '−' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      </section>

        <section className="section portfolio">
          <div className="wrapper grid-display">
            {/* First row: col-8 and col-4 */}
            <div className="row">
              {carouselItems.slice(0, 2).map((item, index) => (
                <div className="portfolio-grid-item" key={item.slug}>
                  <Link
                    to={`/projects/${item.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="portfolio-grid-media">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="portfolio-grid-caption">
                      <div className="portfolio-grid-title">{item.title}</div>
                      <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
            
            {/* Second row: 3xcol-4 items */}
            <div className="row">
              {carouselItems.slice(2, 5).map((item) => (
                <div className="portfolio-grid-item" key={item.slug}>
                  <Link
                    to={`/projects/${item.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="portfolio-grid-media">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="portfolio-grid-caption">
                      <div className="portfolio-grid-title">{item.title}</div>
                      <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Third row: full width col-12 */}
            <div className="row full-height">
              {carouselItems.slice(5, 6).map((item) => (
                <div className="portfolio-grid-item" key={item.slug}>
                  <Link
                    to={`/projects/${item.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="portfolio-grid-media">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="portfolio-grid-caption">
                      <div className="portfolio-grid-title">{item.title}</div>
                      <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

      <section className="section cta">
        <div className="cta-inner">
          {/* Background icons */}
          <img src={newIcon4} alt="CTA decoration" className="cta-icon cta-icon-4" />
          <img src={newIcon5} alt="CTA decoration" className="cta-icon cta-icon-5" />
          <img src={newIcon6} alt="CTA decoration" className="cta-icon cta-icon-6" />
          <img src={newIcon7} alt="CTA decoration" className="cta-icon cta-icon-7" />
          <img src={newIcon8} alt="CTA decoration" className="cta-icon cta-icon-8" />
          
          <h2 className="cta-title">Let's Get started</h2>
          <p className="cta-copy mono">
            Share an idea, brief, budget and timescale — I'll take it from there.
          </p>
          <Link to="/new-project" className="cta-button mono">
            Get in touch
          </Link>
        </div>
      </section>
      </div>
    </>
  );
};

export default Partners;
